import { z } from 'zod';

/**
 * The catalog: every real-world change this system is able to express.
 *
 * THIS FILE IS THE SECURITY BOUNDARY. Not the module toggle, not the role
 * check, not the confirmation dialog — those all gate *whether* an action
 * runs. This gates *what an action can possibly be*, and it is the only one
 * of the five that a convincing model cannot argue its way around.
 *
 * The reason it exists in this shape: the Egnyte MCP server exposes
 * `egnyte_add_permission` and `egnyte_remove_permission`, and alongside them
 * `egnyte_delete`, `egnyte_make_confidential` and `egnyte_upload`. The VP
 * server exposes `vp_delete` and `vp_correct_timecard`. If the unit of
 * authorization were "the Egnyte connection", turning on folder grants would
 * turn on folder deletion in the same movement. So the unit is an entry
 * here: one tool, one argument schema, one rendered sentence, and a named
 * reversal where one exists. A tool with no entry is unreachable.
 *
 * Adding an entry is a security change and should be read as one.
 */

/**
 * An Egnyte path.
 *
 * Anchored to /Shared deliberately. Egnyte's private namespace (/Private/…)
 * is somebody's personal drive, and no ticket workflow has business granting
 * access to it. `..` is refused: the intent of a path containing it is not
 * clear enough to act on.
 *
 * WRITTEN THE WINDOWS WAY IS STILL THE SAME FOLDER. People copy the path from
 * Explorer, where Egnyte is a mapped drive: `Z:\Shared\Projects\Ashford`,
 * or `\shared\whatever`. Backslashes become forward slashes, a leading drive
 * letter is dropped and a missing leading slash is added, so all of those
 * are /Shared/... -- the same normalisation a person would do by eye.
 *
 * CASE-INSENSITIVE on the prefix, and the value is left exactly as typed.
 * Egnyte's paths are not case-sensitive, and requiring `/Shared/` in that
 * exact shape meant a ticket reading "access to /shared/firmwide/CAD
 * Services" could not be acted on at all: the model had no valid argument to
 * offer, and the person filling the form by hand had to discover that the
 * capital S mattered. Refusing a request over the shift key is not a
 * security control, it is a papercut wearing one's clothes.
 */
export function normaliseEgnytePath(raw: string): string {
  let p = raw.trim().replace(/\\/g, '/');
  p = p.replace(/^[a-z]:(?=\/|$)/i, '');
  p = p.replace(/\/{2,}/g, '/');
  if (!p.startsWith('/')) p = `/${p}`;
  return p;
}

const egnytePath = z
  .string()
  .trim()
  .max(500)
  .transform(normaliseEgnytePath)
  .pipe(
    z
      .string()
      .min(8)
      .refine((p) => /^\/shared\//i.test(p), 'must be a path under /Shared/')
      .refine((p) => !p.split('/').includes('..'), 'must not contain ..'),
  );

/**
 * Who an action may be performed for.
 *
 * ESA addresses only. An action that grants a stranger access to a project
 * folder is the outcome this whole feature has to not have, and a ticket
 * requesting it would be exactly what a plausible-sounding phishing mail
 * asks for.
 */
const esaEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .refine((e) => e.endsWith('@esassoc.com'), 'must be an @esassoc.com address');

/** Egnyte's permission ladder, minus Owner. */
const egnyteLevel = z.enum(['Viewer', 'Editor', 'Full']);

/**
 * How much there is to regret.
 *
 * `read` performs no change at all. `low` is reversible by a catalogued
 * action and affects one person. `high` is anything whose reversal is not a
 * single call — nothing here is high yet, and the first entry that is should
 * be argued about on its own merits.
 */
export type ActionRisk = 'read' | 'low' | 'high';

export interface ActionDefinition<S extends z.ZodTypeAny = z.ZodTypeAny> {
  key: string;
  label: string;
  /** What a person is agreeing to, in one sentence they can check. */
  describe: (args: z.infer<S>) => string;
  /** The exact MCP tool. Never chosen at runtime. */
  tool: string;
  /** Which MCP server it lives on, for the executor to route. */
  server: 'egnyte';
  args: S;
  /**
   * What each argument means, for whoever or whatever is filling it in.
   *
   * Written out rather than derived from the Zod schema: the schema knows
   * that `level` is one of three strings, not that choosing the highest one
   * because it is listed last is wrong. These sentences are the only
   * guidance a model gets about intent.
   */
  argHelp: Record<string, string>;
  risk: ActionRisk;
  /**
   * The catalog key that undoes this, where one exists.
   *
   * Recorded so an undo is a catalogued action in its own right rather than
   * a special case that bypasses authorization — undoing is also a real
   * change to a real system and goes through the same gate.
   */
  reversedBy?: string;
  /**
   * A read-only action that shows the current state, for the panel to render
   * beside the proposal. "Grant Bob Editor" reads very differently when Bob
   * already has Full.
   */
  contextTool?: { tool: string; args: (a: z.infer<S>) => Record<string, unknown> };
}

const grantArgs = z.object({
  path: egnytePath,
  email: esaEmail,
  level: egnyteLevel,
});

const revokeArgs = z.object({
  path: egnytePath,
  email: esaEmail,
});

const readArgs = z.object({
  path: egnytePath,
});

const checkArgs = z.object({
  path: egnytePath,
  email: esaEmail,
});

export const ACTION_CATALOG: Readonly<Record<string, ActionDefinition>> = {
  'egnyte.read_permissions': {
    key: 'egnyte.read_permissions',
    label: 'Read Egnyte folder permissions',
    describe: (a: z.infer<typeof readArgs>) => `Read who currently has access to ${a.path}`,
    tool: 'egnyte_get_permissions',
    server: 'egnyte',
    args: readArgs,
    argHelp: {
      path:
        'The Egnyte folder path, copied from the ticket exactly as written. It must be somewhere under /Shared, but the capitalisation does not matter, and Windows-style paths are fine as written: backslashes and a mapped drive such as Z:\\Shared\\... are converted. /shared/firmwide/CAD Services is fine. Do not re-case it, do not tidy it, and do not invent one that is not in the ticket.',
    },
    risk: 'read',
  },

  'egnyte.check_access': {
    key: 'egnyte.check_access',
    label: 'Check one person\u2019s folder access',
    describe: (a: z.infer<typeof checkArgs>) => `Check whether ${a.email} can access ${a.path}`,
    tool: 'egnyte_check_access',
    server: 'egnyte',
    args: checkArgs,
    argHelp: {
      path: 'The Egnyte folder path, copied from the ticket exactly as written. It must be somewhere under /Shared, but the capitalisation does not matter, and Windows-style paths are fine as written: backslashes and a mapped drive such as Z:\\Shared\\... are converted. Do not re-case it, do not tidy it, and do not invent one that is not in the ticket.',
      email: 'The ESA address of the person whose access is in question.',
    },
    risk: 'read',
  },

  'egnyte.folder_access': {
    key: 'egnyte.folder_access',
    label: 'List who can access a folder',
    describe: (a: z.infer<typeof readArgs>) => `List everyone who can access ${a.path}`,
    tool: 'egnyte_folder_access',
    server: 'egnyte',
    args: readArgs,
    argHelp: {
      path: 'The Egnyte folder path, copied from the ticket exactly as written. It must be somewhere under /Shared, but the capitalisation does not matter, and Windows-style paths are fine as written: backslashes and a mapped drive such as Z:\\Shared\\... are converted.',
    },
    risk: 'read',
  },

  'egnyte.grant_access': {
    key: 'egnyte.grant_access',
    label: 'Grant Egnyte folder access',
    describe: (a: z.infer<typeof grantArgs>) =>
      `Grant ${a.email} ${a.level} access to ${a.path}`,
    tool: 'egnyte_add_permission',
    server: 'egnyte',
    args: grantArgs,
    argHelp: {
      path:
        'The folder the person is asking for. The Egnyte folder path, copied from the ticket exactly as written. It must be somewhere under /Shared, but the capitalisation does not matter, and Windows-style paths are fine as written: backslashes and a mapped drive such as Z:\\Shared\\... are converted. /shared/firmwide/CAD Services is fine. Do not re-case it, do not tidy it, and do not invent one that is not in the ticket.',
      email: 'The ESA address of the person who will GAIN access. If the ticket is raised on behalf of a colleague, this is the colleague, not the sender.',
      level:
        'Viewer to read, Editor to read and change, Full for everything. Choose the LOWEST that does what was asked — if they only said they cannot open a file, that is Viewer.',
    },
    risk: 'low',
    reversedBy: 'egnyte.revoke_access',
    contextTool: {
      tool: 'egnyte_check_access',
      args: (a: z.infer<typeof grantArgs>) => ({ path: a.path, email: a.email }),
    },
  },

  'egnyte.revoke_access': {
    key: 'egnyte.revoke_access',
    label: 'Revoke Egnyte folder access',
    describe: (a: z.infer<typeof revokeArgs>) => `Remove ${a.email}'s access to ${a.path}`,
    tool: 'egnyte_remove_permission',
    server: 'egnyte',
    args: revokeArgs,
    argHelp: {
      path:
        'The Egnyte folder path, copied from the ticket exactly as written. It must be somewhere under /Shared, but the capitalisation does not matter, and Windows-style paths are fine as written: backslashes and a mapped drive such as Z:\\Shared\\... are converted. /shared/firmwide/CAD Services is fine. Do not re-case it, do not tidy it, and do not invent one that is not in the ticket.',
      email: 'The ESA address of the person who will LOSE access.',
    },
    risk: 'low',
    reversedBy: 'egnyte.grant_access',
    contextTool: {
      tool: 'egnyte_check_access',
      args: (a: z.infer<typeof revokeArgs>) => ({ path: a.path, email: a.email }),
    },
  },
};

/**
 * The Egnyte username for an ESA address.
 *
 * The local part, lowercased. `pross@esassoc.com` is `pross`, which is what
 * Egnyte reports as a login name. `egnyte_check_access` accepts either form
 * on input, so this is for reading replies, not for building calls.
 */
export function egnyteUsername(email: string): string {
  const local = email.trim().toLowerCase().split('@')[0] ?? '';
  return local.split('+')[0] ?? '';
}

/** Where a confirmed grant lives. */
export interface EgnyteGrant {
  /** On the folder itself, or on a parent it inherits from. */
  type: 'direct' | 'inherited_direct';
  level: string;
  source_folder: string;
}

/** A group that would confer access IF the person is in it — membership unchecked. */
export interface EgnyteGroupGrant {
  group: string;
  level: string;
  source_folder: string;
}

/** What `egnyte_check_access` answers with. */
export interface CheckAccessReply {
  username?: string;
  access?: boolean;
  determinate?: boolean;
  effective_level?: string;
  grants?: EgnyteGrant[];
  unresolved_group_grants?: EgnyteGroupGrant[];
  chain_examined?: string[];
}

/**
 * What we can honestly tell an agent about one person and one folder.
 *
 * FOUR ANSWERS, AND "inconclusive" IS A REAL ONE. The MCP tools resolve
 * direct and inherited grants but cannot expand group membership — that
 * needs an Egnyte scope nobody has yet. So "no direct grant" and "no access"
 * are different statements, and the server tells us which it is rather than
 * leaving us to guess: `determinate` is false exactly when a group on the
 * folder chain might be granting access we cannot see.
 */
export type EgnyteVerdict =
  | {
      verdict: 'has';
      /** Highest confirmed level. A group could only raise it, never lower it. */
      level: string;
      grants: EgnyteGrant[];
      /**
       * Would `egnyte_remove_permission` on THIS folder actually take the
       * access away?
       *
       * Only a `direct` grant whose `source_folder` is the folder in question
       * can be removed there. Access that is inherited lives on a parent, and
       * a revoke against the child removes nothing — while still returning
       * success. That is the failure this field exists to prevent: a ticket
       * closed saying "access removed" to somebody who still has the folder.
       */
      revocableHere: boolean;
      /** Groups that may also grant, and would survive any revoke we perform. */
      unresolvedGroups: EgnyteGroupGrant[];
    }
  /** No direct grant, and no groups on the chain either. Genuinely nothing. */
  | { verdict: 'none' }
  /**
   * No direct grant, but a group on the chain might grant one.
   *
   * NOT a denial. Anything that renders this as "no access" is asserting
   * something nobody checked.
   */
  | { verdict: 'inconclusive'; unresolvedGroups: EgnyteGroupGrant[] }
  /** The question was not answered — the read failed, or was never made. */
  | { verdict: 'unknown' };

/** Folder paths compare case-insensitively, and a trailing slash means nothing. */
function samePath(a: string, b: string): boolean {
  const norm = (p: string) => p.trim().toLowerCase().replace(/\/+$/, '');
  return norm(a) === norm(b);
}

/**
 * Read an `egnyte_check_access` reply for one folder.
 *
 * `path` is the folder the proposal is about, needed to work out whether a
 * confirmed grant is one we could actually remove there.
 */
export function interpretAccess(
  reply: CheckAccessReply | null | undefined,
  path: string,
): EgnyteVerdict {
  if (!reply || typeof reply.access !== 'boolean') return { verdict: 'unknown' };

  const groups = Array.isArray(reply.unresolved_group_grants)
    ? reply.unresolved_group_grants
    : [];

  if (reply.access) {
    const grants = Array.isArray(reply.grants) ? reply.grants : [];
    return {
      verdict: 'has',
      // Trust the server's own highest-wins calculation over re-deriving it.
      level: reply.effective_level ?? highestLevel(grants.map((g) => g.level)) ?? 'unknown',
      grants,
      revocableHere: grants.some((g) => g.type === 'direct' && samePath(g.source_folder, path)),
      unresolvedGroups: groups,
    };
  }

  // access:false. `determinate` decides whether that is an answer or a gap.
  if (reply.determinate === true && groups.length === 0) return { verdict: 'none' };
  return { verdict: 'inconclusive', unresolvedGroups: groups };
}

/**
 * Egnyte's ladder, low to high.
 *
 * `Preview` sits below Viewer and `None` is the absence of access; both can
 * come back from Egnyte even though neither is something we grant.
 */
const LADDER = ['None', 'Preview', 'Viewer', 'Editor', 'Full', 'Owner'];

/** The highest of several levels, or null if none is recognised. */
export function highestLevel(levels: readonly string[]): string | null {
  let best: string | null = null;
  let bestRank = -1;
  for (const l of levels) {
    const rank = LADDER.findIndex((x) => x.toLowerCase() === l.trim().toLowerCase());
    if (rank > bestRank) {
      bestRank = rank;
      best = LADDER[rank] ?? null;
    }
  }
  return best;
}

/**
 * Is this a level our catalogue can grant?
 *
 * `Owner` is reported by Egnyte and is deliberately outside the grant enum,
 * as are `Preview` and `None`. An agent can be shown that somebody is an
 * Owner; offering to move them off it is a different decision from the one
 * this feature makes.
 */
export function isProposableLevel(level: string): boolean {
  return level === 'Viewer' || level === 'Editor' || level === 'Full';
}

export const ACTION_KEYS = Object.keys(ACTION_CATALOG);

export function actionDefinition(key: string): ActionDefinition | null {
  return Object.prototype.hasOwnProperty.call(ACTION_CATALOG, key)
    ? ACTION_CATALOG[key]!
    : null;
}

/**
 * Validate arguments against the catalog entry and render what they mean.
 *
 * One function because the two must not drift: the sentence a person
 * authorizes has to be produced from the same parsed values that will be
 * sent to the tool. Rendering from the raw input and executing from the
 * parsed output is how "Editor" on screen becomes "Full" on the wire.
 */
export function parseActionArgs(
  key: string,
  raw: unknown,
): { ok: true; args: Record<string, unknown>; preview: string } | { ok: false; error: string } {
  const def = actionDefinition(key);
  if (!def) return { ok: false, error: `no such action: ${key}` };

  const parsed = def.args.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    return {
      ok: false,
      error: first ? `${first.path.join('.') || 'args'}: ${first.message}` : 'invalid arguments',
    };
  }

  return {
    ok: true,
    args: parsed.data as Record<string, unknown>,
    preview: def.describe(parsed.data),
  };
}

/**
 * A stable digest of the arguments, for comparing what was shown with what
 * is about to run.
 *
 * Keys are sorted so that a re-serialisation with a different property order
 * produces the same digest — otherwise the executor would reject perfectly
 * good authorizations depending on how the JSON happened to round-trip.
 */
export function canonicalArgs(args: Record<string, unknown>): string {
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(args).sort()) sorted[k] = args[k];
  return JSON.stringify(sorted);
}
